import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import SeriesPage from "./pages/SeriesPage";
import CompanionPage from "./pages/CompanionPage";
import ClarityPage from "./pages/ClarityPage";
import ReviewHeroPage from "./pages/ReviewHeroPage";
import VisibilityHeroPage from "./pages/VisibilityHeroPage";
import InsightHeroPage from "./pages/InsightHeroPage";
import CareerHeroPage from "./pages/CareerHeroPage";
import OverwhelmHeroPage from "./pages/OverwhelmHeroPage";
import ValueCompletionPage from "./pages/ValueCompletionPage";
import QRCodePage from "./pages/QRCodePage";
import TeamsLandingPage from "./pages/TeamsLandingPage";
import JoinTeamPage from "./pages/teams/JoinTeamPage";
import TeamSignupPage from "./pages/teams/TeamSignupPage";
import RedeemCodePage from "./pages/RedeemCodePage";
import AdminPage from "./pages/admin/AdminPage";
import AdminBooksPage from "./pages/admin/AdminBooksPage";
import { getDefaultSeries } from "./data/series";
import { 
  TermsOfUse, 
  PrivacyPolicy, 
  TermsOfSale, 
  CompanionTerms, 
  CompanionPrivacy,
  DataRequestPage
} from "./pages/policies";
import SituationsHubPage from "./pages/SituationsHubPage";
import SituationDetailPage from "./pages/SituationDetailPage";
import CookieConsentBanner from "./components/legal/CookieConsentBanner";
import { 
  LoginPage, 
  VerifyPage, 
  DashboardPage,
  PeoplePage,
  PersonDetailPage,
  ProjectsPage,
  ProjectDetailPage,
  ReflectionsPage,
  ReflectionDetailPage,
  ChatPage,
  AccomplishmentsPage,
  SettingsPage,
  TeamsPage,
  TeamDetailPage,
  LivingCVPage,
  SituationsPage,
  PerformanceReviewPage,
  AreasPage,
  AreaDetailPage
} from "./pages/dashboard";
import { AuthProvider } from "./contexts/AuthContext";
import { HelmetProvider } from "react-helmet-async";
import RouteSEO from "./components/seo/RouteSEO";

// Redirect component for homepage
const HomeRedirect = () => {
  const defaultSeries = getDefaultSeries();
  return <Navigate to={`/${defaultSeries?.slug || 'it-leadership'}`} replace />;
};

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <AuthProvider>
          <BrowserRouter>
            <RouteSEO />
          <Routes>
            {/* Homepage redirects to default series (IT Leadership for now) */}
            <Route path="/" element={<HomeRedirect />} />
            
            {/* QR Code / Deep Link handler */}
            <Route path="/r/:code" element={<QRCodePage />} />
            
            {/* Dashboard routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/verify/:token" element={<VerifyPage />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/dashboard/people" element={<PeoplePage />} />
            <Route path="/dashboard/people/:alias" element={<PersonDetailPage />} />
            <Route path="/dashboard/projects" element={<ProjectsPage />} />
            <Route path="/dashboard/projects/:alias" element={<ProjectDetailPage />} />
            <Route path="/dashboard/reflections" element={<ReflectionsPage />} />
            <Route path="/dashboard/reflections/:conversationId" element={<ReflectionDetailPage />} />
            <Route path="/dashboard/chat" element={<ChatPage />} />
            <Route path="/dashboard/accomplishments" element={<AccomplishmentsPage />} />
            <Route path="/dashboard/settings" element={<SettingsPage />} />
            <Route path="/dashboard/teams" element={<TeamsPage />} />
            <Route path="/dashboard/teams/:teamId" element={<TeamDetailPage />} />
            <Route path="/dashboard/living-cv" element={<LivingCVPage />} />
            <Route path="/dashboard/situations" element={<SituationsPage />} />
            <Route path="/dashboard/review" element={<PerformanceReviewPage />} />
            <Route path="/dashboard/areas" element={<AreasPage />} />
            <Route path="/dashboard/areas/:areaId" element={<AreaDetailPage />} />

            {/* Hidden admin console — not linked from any nav. Access: /admin */}
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/admin/books" element={<AdminBooksPage />} />
            
            {/* Companion info page */}
            <Route path="/companion" element={<CompanionPage />} />
            
            {/* Situational Clarity entry point (U3) */}
            <Route path="/clarity" element={<ClarityPage />} />
            
            {/* HP.2: Performance Review Hero Page */}
            <Route path="/review" element={<ReviewHeroPage />} />
            
            {/* HP.1: Impact Visibility Hero Page */}
            <Route path="/visibility" element={<VisibilityHeroPage />} />
            
            {/* HP.3: Capability Insight Hero Page */}
            <Route path="/insight" element={<InsightHeroPage />} />
            
            {/* HP.4: Career Decision Hero Page */}
            <Route path="/career" element={<CareerHeroPage />} />
            <Route path="/overwhelm" element={<OverwhelmHeroPage />} />
            
            {/* RV.4: Value Completion Page (generic, works for all hero flows) */}
            <Route path="/review/complete" element={<ValueCompletionPage />} />
            <Route path="/visibility/complete" element={<ValueCompletionPage />} />
            <Route path="/insight/complete" element={<ValueCompletionPage />} />
            <Route path="/career/complete" element={<ValueCompletionPage />} />
            <Route path="/overwhelm/complete" element={<ValueCompletionPage />} />
            <Route path="/clarity/complete" element={<ValueCompletionPage />} />
            
            {/* Teams/Founders landing page */}
            <Route path="/teams" element={<TeamsLandingPage />} />
            
            {/* Team signup flow (E0.2) */}
            <Route path="/teams/signup" element={<TeamSignupPage />} />
            
            {/* Team invite acceptance page */}
            <Route path="/teams/join/:token" element={<JoinTeamPage />} />
            
            {/* Bundle code redemption page */}
            <Route path="/redeem" element={<RedeemCodePage />} />
            
            {/* Policy pages */}
            <Route path="/terms" element={<TermsOfUse />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-sale" element={<TermsOfSale />} />
            <Route path="/companion-terms" element={<CompanionTerms />} />
            <Route path="/companion-privacy" element={<CompanionPrivacy />} />
            <Route path="/data-request" element={<DataRequestPage />} />

            {/* ANS.1: Situations Hub — public knowledge bank */}
            <Route path="/situations" element={<SituationsHubPage />} />
            <Route path="/situations/:slug" element={<SituationDetailPage />} />
            
            {/* Series pages - must be last as it's a catch-all pattern */}
            <Route path="/:seriesSlug" element={<SeriesPage />} />
            
            {/* Catch-all redirect to home */}
            <Route path="*" element={<HomeRedirect />} />
          </Routes>
          <CookieConsentBanner />
        </BrowserRouter>
      </AuthProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
